import { useEffect, useState } from "react";
import { DeviceMobile, X, Download, Share as ShareIcon } from "@phosphor-icons/react";

const DISMISS_KEY = "jw_pwa_dismissed";

/**
 * Floating "Install our app" prompt.
 * - On Chrome / Android / desktop: listens for `beforeinstallprompt` and shows
 *   a native-style install button.
 * - On iOS Safari (which doesn't support beforeinstallprompt): shows a hint to
 *   tap the Share button → "Add to Home Screen".
 * The banner respects a per-device dismiss flag stored in localStorage.
 */
export default function PWAInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [iosMode, setIosMode] = useState(false);

    useEffect(() => {
        if (localStorage.getItem(DISMISS_KEY) === "1") return;
        if (window.matchMedia("(display-mode: standalone)").matches) return;
        if (window.navigator.standalone) return;

        const ua = window.navigator.userAgent || "";
        const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isMobile = /Mobi|Android|iPhone|iPad/.test(ua);

        if (isIos) {
            setIosMode(true);
            const t = setTimeout(() => setVisible(true), 4000);
            return () => clearTimeout(t);
        }

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
            if (isMobile) setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        return () => window.removeEventListener("beforeinstallprompt", onPrompt);
    }, []);

    if (!visible) return null;

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") {
            localStorage.setItem(DISMISS_KEY, "1");
        }
        setVisible(false);
        setDeferred(null);
    };

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, "1");
        setVisible(false);
    };

    return (
        <div
            data-testid="pwa-install-banner"
            className="fixed bottom-4 inset-x-4 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm rounded-2xl bg-foreground text-background shadow-2xl px-4 py-3 flex items-start gap-3"
            role="dialog"
        >
            <span className="grid place-items-center h-10 w-10 rounded-full bg-primary text-primary-foreground shrink-0">
                <DeviceMobile size={20} weight="fill" />
            </span>
            <div className="flex-1 min-w-0">
                <p className="font-display font-black leading-none">Instala la app de J&amp;W Deli</p>
                {iosMode ? (
                    <p className="text-xs text-background/80 mt-1">
                        Toca <ShareIcon size={12} weight="fill" className="inline mx-0.5" /> y luego &ldquo;Agregar a Pantalla de inicio&rdquo;.
                    </p>
                ) : (
                    <p className="text-xs text-background/80 mt-1">
                        Ordena y rastrea tus pedidos en vivo desde tu teléfono.
                    </p>
                )}
                {!iosMode && (
                    <button
                        data-testid="pwa-install-btn"
                        onClick={install}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary hover:bg-primary/90 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary-foreground"
                    >
                        <Download size={12} weight="bold" /> Instalar
                    </button>
                )}
            </div>
            <button
                data-testid="pwa-install-dismiss"
                onClick={dismiss}
                className="h-7 w-7 grid place-items-center rounded-full hover:bg-white/10 shrink-0"
                aria-label="Cerrar"
            >
                <X size={14} weight="bold" />
            </button>
        </div>
    );
}
